import type { NotificationProvider } from '@refinedev/core';
import { useNotificationProvider } from '@refinedev/antd';
import { App, notification as staticNotification } from 'antd';
import i18n from '@/providers/i18nProvider';

/**
 * Refine's antd provider with one layout for every toast: a constant heading
 * per type ("Sukces", "Błąd") and the detail as the body.
 *
 * Producers disagree on which field holds the detail. Refine's built-in
 * success toast puts the constant in ``description`` and the detail
 * ("Successfully updated …") in ``message``; its error toast and our call
 * sites put the heading in ``message`` and the detail (the backend's reasons)
 * in ``description``. So the heading is derived from the type, and the body is
 * whichever field is not that constant — ``description`` first.
 */
export const useAppNotificationProvider = (): NotificationProvider => {
  const base = useNotificationProvider();
  const { notification: fromContext } = App.useApp();
  // Same fallback as the stock provider when used outside antd's <App>.
  const notification = 'open' in fromContext ? fromContext : staticNotification;

  return {
    ...base,
    open: (params) => {
      if (params.type === 'progress') return base.open(params);
      const heading = i18n.t(`notifications.${params.type}`);
      const detail =
        params.description && params.description !== heading
          ? params.description
          : params.message;
      notification.open({
        key: params.key,
        type: params.type,
        message: heading,
        description: detail === heading ? undefined : detail,
        // One backend error per line (see ``extractDetailMessage``).
        style: { whiteSpace: 'pre-line' },
      });
    },
  };
};
